import { Stack, Alert } from '@mui/material';
import { getFragmentData } from '@api/gql';
import { usePagePagination } from '@common/hooks/use-page-pagination';

import { standingAccessReportEntitlementFragmentDocument } from '@StandingAccess/services/gql';
import { useGetStandingAccessReportEntitlements } from '@StandingAccess/services/standingAccessQueries';
import AssessmentReportTable, {
  AssessmentReportTableItem,
  AssessmentReportTableItemLoadingRow,
} from '@StandingAccess/components/AssessmentReportTable';
import TablePagination from '@components/TablePagination';
import { useSearchParams } from 'react-router-dom';
import { StandingAccessReportEntitlementFragmentFragment } from '@api/gql/graphql';
import { REPORT_ITEM_PARAM } from '@StandingAccess/types';
import { useReportFilters } from '@StandingAccess/hooks/use-report-filters';
import { useFlagFixed } from '@hooks';
import { Flag } from '@utils';
import AssessmentReportItemRemediation from '@StandingAccess/components/AssessmentReportItemRemediation';

const usePaginatedReport = (reportId: string) => {
  const { offset, pageSize, handlePageChange } = usePagePagination();
  const filter = useReportFilters();

  const { data, isFetched, isFetching } = useGetStandingAccessReportEntitlements({
    reportId,
    filter,
    pagination: { offset, take: pageSize },
  });

  return {
    data,
    pageSize,
    isFetched,
    isFetching,
    offset,
    handlePageChange,
  };
};

interface AssessmentReportProps {
  reportId: string;
}

export default function AssessmentReport({ reportId }: AssessmentReportProps) {
  const [searchParams, setSearchParams] = useSearchParams();
  const { data, pageSize, isFetched, isFetching, offset, handlePageChange } = usePaginatedReport(reportId);
  const { isEnabled: isAccessFlowEnabled } = useFlagFixed(Flag.ASSESSMENT_REPORTS_ACCESS_FLOW);

  const remediationTitle = isAccessFlowEnabled ? 'Remediation Progress' : 'Revocation Progress';

  if (!isFetched) {
    return (
      <AssessmentReportTable remediationTitle={remediationTitle}>
        <AssessmentReportTableItemLoadingRow />
        <AssessmentReportTableItemLoadingRow />
        <AssessmentReportTableItemLoadingRow />
      </AssessmentReportTable>
    );
  }

  if (!data) return <Alert severity="error">No data found</Alert>;

  const { totalCount, edges } = data.standingAccessReportEntitlements;
  const items = edges.map((edge) => getFragmentData(standingAccessReportEntitlementFragmentDocument, edge.node));

  const handleItemClick = (item: StandingAccessReportEntitlementFragmentFragment) => {
    searchParams.set(REPORT_ITEM_PARAM, item.id);
    setSearchParams(searchParams, { replace: true });
  };

  return (
    <Stack direction="column" justifyContent="center" alignItems="stretch" spacing={2}>
      <AssessmentReportTable isLoading={isFetching} remediationTitle={remediationTitle}>
        {items.map((item) => (
          <AssessmentReportTableItem
            key={item.id}
            item={item}
            onClick={() => handleItemClick(item)}
            selected={item.id === searchParams.get(REPORT_ITEM_PARAM)}
            remediationProgress={
              <AssessmentReportItemRemediation
                inAccessFlow={isAccessFlowEnabled ? item.inAccessFlowCount : undefined}
                revoked={item.revokedCount}
                total={item.identitiesCount}
              />
            }
          />
        ))}
      </AssessmentReportTable>
      <TablePagination
        pagination={{
          total: totalCount,
          limit: pageSize,
          offset,
        }}
        isLoading={isFetching}
        onChange={handlePageChange}
      />
    </Stack>
  );
}
